import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { Modal } from '@/components/synapse/Modal';
import { Select } from '@/components/synapse/Select';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import {
  ITEM_TYPE_LABEL_KEY,
  ITEM_TYPES,
  isFieldRequired,
  isFieldVisible,
  isKnownItemType,
} from '@/lib/itemTypeConfig';
import type { ItemType } from '@/lib/itemTypeConfig';
import type { StockMovementLogRow, UpdateMovementPayload } from '@/types/api';

// Mirrors the Adjust page's chips, so a reason edited here reads the same as
// the reason chosen there.
const ADJUSTMENT_REASONS = [
  { value: 'expired', labelKey: 'adjust.reasonExpired' },
  { value: 'damaged', labelKey: 'adjust.reasonDamaged' },
  { value: 'discarded', labelKey: 'adjust.reasonDiscarded' },
  { value: 'other', labelKey: 'adjust.reasonOther' },
] as const;

interface EditMovementDialogProps {
  row: StockMovementLogRow;
  onClose: () => void;
  onSubmit: (patch: UpdateMovementPayload) => void;
  isPending: boolean;
  error: unknown;
}

/** Empty string means "cleared" for a nullable text column. */
function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Correct one ledger line.
 *
 * Two shapes in one dialog, because the server accepts two shapes: a stock-in
 * is the movement that created its batch, so it can edit what arrived — the
 * quantity, the batch descriptors, and the item itself. An issue or adjustment
 * only referenced an existing batch, so it can edit how much left and why.
 * Offering the item fields there would look like a local correction and act
 * like a rewrite of every other entry drawn from that batch.
 *
 * Purpose-built rather than reusing AddStockPage's draft editor: that grid is
 * wired to barcode-scan state, suggestion-linking that unlinks the item on every
 * keystroke, and an add-to-batch flow — none of which apply once the item
 * exists. It also needs context the add form has no notion of, like the balance
 * this batch is sitting at.
 */
export function EditMovementDialog({
  row,
  onClose,
  onSubmit,
  isPending,
  error,
}: EditMovementDialogProps) {
  const { t } = useTranslation();
  const isStockIn = row.kind === 'STOCK_IN';
  const isAdjustment = row.kind === 'ADJUSTMENT';

  // Adjustments carry their sign as meaning; the other kinds get it from their
  // kind, so the field shows the magnitude the user thinks in.
  const [quantity, setQuantity] = useState(
    String(isAdjustment ? row.quantity : Math.abs(row.quantity)),
  );
  const [note, setNote] = useState(row.note ?? '');
  const [reason, setReason] = useState(row.reason ?? '');
  const [batchNumber, setBatchNumber] = useState(row.batchNumber ?? '');
  const [expiryDate, setExpiryDate] = useState(row.expiryDate ?? '');
  const [type, setType] = useState<ItemType>(
    isKnownItemType(row.itemType) ? row.itemType : 'other',
  );
  const [brandName, setBrandName] = useState(row.itemBrandName);
  const [genericName, setGenericName] = useState(row.itemGenericName ?? '');
  const [strength, setStrength] = useState(row.itemStrength ?? '');
  const [unit, setUnit] = useState(row.itemUnit);
  // Not shown on the row, so there is nothing to seed them from. Left blank,
  // they read as "unchanged" in buildPatch and are never sent — except when the
  // new type requires one, which is the case this exists for (a reagent needs a
  // storage condition, and without a control the server's rejection is a dead
  // end the user cannot act on).
  const [manufacturer, setManufacturer] = useState('');
  const [storageCondition, setStorageCondition] = useState('');

  const parsedQuantity = Number(quantity);
  // Only an adjustment's sign carries meaning. For the other kinds the field is
  // a magnitude, and a negative would be silently flipped by the server — the
  // dialog would close reporting success having changed nothing.
  const quantityValid =
    Number.isInteger(parsedQuantity) &&
    parsedQuantity !== 0 &&
    (isAdjustment || parsedQuantity > 0);

  /**
   * Send only what actually changed.
   *
   * A patch is a set of intentions, not a snapshot: resubmitting every field
   * would make an unrelated edit look like a rename, and would trip the
   * server's "item type can't change while other batches reference this item"
   * guard on an item whose type the user never touched.
   */
  function buildPatch(): UpdateMovementPayload {
    const patch: UpdateMovementPayload = {};

    const originalQuantity = isAdjustment ? row.quantity : Math.abs(row.quantity);
    if (parsedQuantity !== originalQuantity) patch.quantity = parsedQuantity;

    if (nullable(note) !== (row.note ?? null)) patch.note = nullable(note);
    if (isAdjustment && reason.trim() !== '' && reason.trim() !== (row.reason ?? '')) {
      patch.reason = reason.trim();
    }

    if (isStockIn) {
      const batch: NonNullable<UpdateMovementPayload['batch']> = {};
      if (nullable(batchNumber) !== (row.batchNumber ?? null)) {
        batch.batchNumber = nullable(batchNumber);
      }
      if (nullable(expiryDate) !== (row.expiryDate ?? null)) {
        batch.expiryDate = nullable(expiryDate);
      }
      if (Object.keys(batch).length > 0) patch.batch = batch;

      const item: NonNullable<UpdateMovementPayload['item']> = {};
      if (type !== row.itemType) item.type = type;
      if (brandName.trim() !== row.itemBrandName) item.brandName = brandName.trim();
      if (nullable(genericName) !== (row.itemGenericName ?? null)) {
        item.genericName = nullable(genericName);
      }
      if (nullable(strength) !== (row.itemStrength ?? null)) item.strength = nullable(strength);
      if (unit.trim() !== row.itemUnit) item.unit = unit.trim();
      // Blank means untouched here, not cleared: the row never carried these, so
      // there is no stored value to compare against.
      if (nullable(manufacturer)) item.manufacturer = nullable(manufacturer);
      if (nullable(storageCondition)) item.storageCondition = nullable(storageCondition);
      if (Object.keys(item).length > 0) patch.item = item;
    }

    return patch;
  }

  const patch = buildPatch();
  const hasChanges = Object.keys(patch).length > 0;
  const canSubmit = hasChanges && quantityValid && brandName.trim() !== '' && unit.trim() !== '';

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit(patch);
  }

  return (
    <Modal
      title={t('activity.editTitle')}
      description={
        isStockIn ? t('activity.editStockInDescription') : t('activity.editIssueDescription')
      }
      onClose={onClose}
      isPending={isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending} full>
            {t('activity.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending} full>
            {isPending ? t('activity.saving') : t('activity.save')}
          </Button>
        </>
      }
    >
      <Field label={t('common.quantity')}>
        {(id) => (
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            min={isAdjustment ? undefined : 1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        )}
      </Field>

      {isAdjustment && (
        <Field label={t('activity.reason')}>
          {(id) => (
            <Select id={id} value={reason} onChange={(e) => setReason(e.target.value)}>
              {/* The column is free text, so a stored reason need not be one of
                  ours. Without this the control would silently display a
                  different reason than the movement actually carries. */}
              {!ADJUSTMENT_REASONS.some((r) => r.value === reason) && reason !== '' && (
                <option value={reason}>{reason}</option>
              )}
              {ADJUSTMENT_REASONS.map(({ value, labelKey }) => (
                <option key={value} value={value}>
                  {t(labelKey)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <Field label={t('activity.note')} optional>
        {(id) => (
          <Input
            id={id}
            value={note}
            placeholder={t('activity.notePlaceholder')}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
      </Field>

      {isStockIn && (
        <>
          <div className="mt-1 text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
            {t('activity.batchSection')}
          </div>
          {isFieldVisible(type, 'batchNumber') && (
            <Field label={t('common.batchNumber')} optional>
              {(id) => (
                <Input
                  id={id}
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                />
              )}
            </Field>
          )}
          {isFieldVisible(type, 'expiryDate') && (
            <Field label={t('activity.expiryDate')} optional>
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              )}
            </Field>
          )}

          <div className="mt-1 text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
            {t('activity.itemSection')}
          </div>
          <Field label={t('addStock.itemType')}>
            {(id) => (
              <Select
                id={id}
                value={type}
                onChange={(e) => setType(e.target.value as ItemType)}
              >
                {ITEM_TYPES.map((it) => (
                  <option key={it} value={it}>
                    {t(ITEM_TYPE_LABEL_KEY[it])}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('common.brandName')}>
            {(id) => (
              <Input id={id} value={brandName} onChange={(e) => setBrandName(e.target.value)} />
            )}
          </Field>
          {isFieldVisible(type, 'genericName') && (
            <Field label={t('common.genericName')}>
              {(id) => (
                <Input
                  id={id}
                  value={genericName}
                  onChange={(e) => setGenericName(e.target.value)}
                />
              )}
            </Field>
          )}
          {isFieldVisible(type, 'strength') && (
            <Field label={t('common.strength')}>
              {(id) => (
                <Input id={id} value={strength} onChange={(e) => setStrength(e.target.value)} />
              )}
            </Field>
          )}
          <Field label={t('common.unit')}>
            {(id) => <Input id={id} value={unit} onChange={(e) => setUnit(e.target.value)} />}
          </Field>
          {isFieldVisible(type, 'manufacturer') && (
            <Field label={t('common.manufacturer')} optional>
              {(id) => (
                <Input
                  id={id}
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                />
              )}
            </Field>
          )}
          {isFieldVisible(type, 'storageCondition') && (
            <Field
              label={t('common.storageCondition')}
              optional={!isFieldRequired(type, 'storageCondition')}
            >
              {(id) => (
                <Input
                  id={id}
                  value={storageCondition}
                  onChange={(e) => setStorageCondition(e.target.value)}
                />
              )}
            </Field>
          )}
        </>
      )}

      {!hasChanges && <p className="text-[12px] text-muted">{t('activity.noChanges')}</p>}

      {error != null && (
        <p
          role="alert"
          className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive"
        >
          {error instanceof ApiError ? translateApiError(error) : t('errors.fallback')}
        </p>
      )}
    </Modal>
  );
}
